import React, { useEffect, useState } from 'react'
import { Constants, GlobalConfig, I18n } from 'galarm-config'
import { AlarmUtils } from 'galarm-shared'
import { Box, Button, List, Spinner, Text } from 'grommet'
import { FormNext, FormPrevious, FormRefresh } from 'grommet-icons'
import { onSnapshot } from 'firebase/firestore'
import UnifiedAlarmSummary from './UnifiedAlarmSummary'
import SearchBar from './SearchBar'

const AlertIncidents = ({ showSearchBar, hideSearchBar }) => {
  const [searchText, setSearchText] = useState('')
  const [alertIncidents, setAlertIncidents] = useState([])
  const [numIncidents, setNumIncidents] = useState(0)
  const [currentStartIndex, setCurrentStartIndex] = useState(1)
  const [lastVisible, setLastVisible] = useState(null)
  const [firstVisible, setFirstVisible] = useState(null)
  const [loading, setLoading] = useState(false)
  const pageSize = 10
  let unsubscribe = null

  useEffect(() => {
    if (!showSearchBar) {
      setSearchText('')
    }
  }, [showSearchBar])

  const setupListener = queryInstance => {
    if (unsubscribe) unsubscribe()

    unsubscribe = onSnapshot(queryInstance, snapshot => {
      fetchTotalIncidentsCount()
      if (!snapshot.empty) {
        setFirstVisible(snapshot.docs[0])
        setLastVisible(snapshot.docs[snapshot.docs.length - 1])
        setAlertIncidents(
          snapshot.docs.map(doc => {
            const alarm = AlarmUtils.createAlarmFromFirestoreData(doc.data())
            return {
              ...alarm,
              alarmCategory: Constants.AlarmCategories.PARTICIPANT_ALARM
            }
          })
        )
      } else {
        setAlertIncidents([])
      }
    })
  }

  useEffect(() => {
    fetchInitialIncidents()

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [searchText])

  const goToFirstPage = () => {
    fetchInitialIncidents()
  }

  const fetchTotalIncidentsCount = async () => {
    try {
      const incidentAlarmsCollection =
        GlobalConfig.firebaseHelpers.collectionRef(
          `userInfos/${GlobalConfig.uid}/incidentAlarms`
        )
      let queryRef = incidentAlarmsCollection
      if (searchText) {
        const whereConstraint1 = GlobalConfig.firebaseHelpers.where(
          'name',
          '>=',
          searchText
        )
        const whereConstraint2 = GlobalConfig.firebaseHelpers.where(
          'name',
          '<=',
          searchText + '\uf8ff'
        )
        queryRef = GlobalConfig.firebaseHelpers.createFirestoreQuery(
          incidentAlarmsCollection,
          whereConstraint1,
          whereConstraint2
        )
      }
      const totalIncidentCountSnapshot =
        await GlobalConfig.firebaseHelpers.getCountFromServer(queryRef)
      setNumIncidents(totalIncidentCountSnapshot.data().count)
    } catch (error) {
      console.error('Error fetching total incidents: ', error)
    }
  }

  const fetchInitialIncidents = async () => {
    setLoading(true)
    const incidentAlarmsCollection = GlobalConfig.firebaseHelpers.collectionRef(
      `userInfos/${GlobalConfig.uid}/incidentAlarms`
    )

    let constraints = []

    if (searchText) {
      const whereConstraint1 = GlobalConfig.firebaseHelpers.where(
        'name',
        '>=',
        searchText
      )
      const whereConstraint2 = GlobalConfig.firebaseHelpers.where(
        'name',
        '<=',
        searchText + '\uf8ff'
      )
      const orderByName = GlobalConfig.firebaseHelpers.orderBy('name')
      constraints.push(whereConstraint1, whereConstraint2, orderByName)
    }

    const orderByDate = GlobalConfig.firebaseHelpers.orderBy('date', 'desc')
    const limitConstraint = GlobalConfig.firebaseHelpers.limit(pageSize)
    constraints.push(orderByDate, limitConstraint)

    const queryRef = GlobalConfig.firebaseHelpers.createFirestoreQuery(
      incidentAlarmsCollection,
      ...constraints
    )

    setupListener(queryRef)
    setCurrentStartIndex(1)
    setLoading(false)
  }

  const fetchNextPage = async () => {
    if (!lastVisible) return
    setLoading(true)
    const incidentAlarmsCollection = GlobalConfig.firebaseHelpers.collectionRef(
      `userInfos/${GlobalConfig.uid}/incidentAlarms`
    )

    let constraints = []

    if (searchText) {
      const whereConstraint1 = GlobalConfig.firebaseHelpers.where(
        'name',
        '>=',
        searchText
      )
      const whereConstraint2 = GlobalConfig.firebaseHelpers.where(
        'name',
        '<=',
        searchText + '\uf8ff'
      )
      const orderByName = GlobalConfig.firebaseHelpers.orderBy('name')
      constraints.push(whereConstraint1, whereConstraint2, orderByName)
    }

    const orderByDate = GlobalConfig.firebaseHelpers.orderBy('date', 'desc')
    const startAfterConstraint =
      GlobalConfig.firebaseHelpers.firestoreStartAfter(lastVisible)
    const limitConstraint = GlobalConfig.firebaseHelpers.limit(pageSize)
    constraints.push(orderByDate, startAfterConstraint, limitConstraint)

    const queryRef = GlobalConfig.firebaseHelpers.createFirestoreQuery(
      incidentAlarmsCollection,
      ...constraints
    )

    setupListener(queryRef)
    setCurrentStartIndex(currentStartIndex + pageSize)
    setLoading(false)
  }

  const fetchPreviousPage = async () => {
    if (!firstVisible) return
    setLoading(true)
    const incidentAlarmsCollection = GlobalConfig.firebaseHelpers.collectionRef(
      `userInfos/${GlobalConfig.uid}/incidentAlarms`
    )

    let constraints = []

    if (searchText) {
      const whereConstraint1 = GlobalConfig.firebaseHelpers.where(
        'name',
        '>=',
        searchText
      )
      const whereConstraint2 = GlobalConfig.firebaseHelpers.where(
        'name',
        '<=',
        searchText + '\uf8ff'
      )
      const orderByName = GlobalConfig.firebaseHelpers.orderBy('name')
      constraints.push(whereConstraint1, whereConstraint2, orderByName)
    }

    const orderByDate = GlobalConfig.firebaseHelpers.orderBy('date', 'desc')
    const endBeforeConstraint =
      GlobalConfig.firebaseHelpers.firestoreEndBefore(firstVisible)
    const limitToLastConstraint =
      GlobalConfig.firebaseHelpers.firestoreLimitToLast(pageSize)
    constraints.push(orderByDate, endBeforeConstraint, limitToLastConstraint)

    const queryRef = GlobalConfig.firebaseHelpers.createFirestoreQuery(
      incidentAlarmsCollection,
      ...constraints
    )

    setupListener(queryRef)
    setCurrentStartIndex(Math.max(1, currentStartIndex - pageSize))
    setLoading(false)
  }

  return (
    <Box flex={{ shrink: 0 }}>
      {showSearchBar && (
        <SearchBar
          onChangeText={setSearchText}
          searchText={searchText}
          onCloseSearchBar={hideSearchBar}
          focus
        />
      )}
      <Box direction="row" align="center" justify="between">
        <Box direction="row" align="center">
          <Button
            onClick={goToFirstPage}
            icon={<FormRefresh color="primary" />}
          />
          <Button
            onClick={fetchPreviousPage}
            disabled={currentStartIndex === 1 || loading}
            icon={<FormPrevious color="primary" />}
          />
        </Box>
        <Text size="small" margin={{ horizontal: 'small' }} color="darkTint">
          {I18n.t('showingIncidents', {
            startIndex: currentStartIndex,
            endIndex: Math.min(
              currentStartIndex + alertIncidents.length - 1,
              numIncidents
            ),
            count: numIncidents
          })}
        </Text>
        <Button
          onClick={fetchNextPage}
          disabled={
            currentStartIndex + alertIncidents.length - 1 >= numIncidents ||
            loading
          }
          icon={<FormNext color="primary" />}
        />
      </Box>
      {loading ? (
        <Box justify="center" align="center">
          <Spinner />
        </Box>
      ) : alertIncidents.length === 0 ? (
        <Box pad="medium" flex justify="center" align="center">
          <Text color="textColor">{I18n.t('noIncidentsFound')}</Text>
        </Box>
      ) : (
        <List
          border={false}
          pad={{ vertical: 'xsmall', horizontal: 'small' }}
          data={alertIncidents}
          // eslint-disable-next-line react/no-children-prop
          children={UnifiedAlarmSummary}
        />
      )}
    </Box>
  )
}

export default AlertIncidents
